/**
 * Publication figure: scatter of cities & counties — cost effectiveness vs per-capita abatement.
 * Data is read from Supabase table publication_abatement_scatter (precomputed by ETL script).
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  PointElement,
  ScatterController,
  Tooltip,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Scatter } from 'react-chartjs-2';
import type { BuildingsBauScenario } from '../../services/buildingsBauPathways';
import { PUBLICATION_ANALYSIS_CHART_IDS } from '../../config/publicationAnalysisChartIds';
import { ChartPngPageIdOverrideContext, useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { PublicationFigureDownloads } from '../publication/PublicationFigureDownloads';
import {
  fetchPublicationAbatementScatterRows,
  PUBLICATION_ABATEMENT_POLICY_PRESET,
  type PublicationAbatementScatterRow,
} from '../../services/publicationAbatementScatterService';
import {
  downloadChartJsInstanceAsPNG,
  downloadDataAsCSV,
  formatFilename,
} from '../../utils/downloadUtils';

ChartJS.register(LinearScale, PointElement, ScatterController, Tooltip, Legend, zoomPlugin);

type AbatementScatterEmfacVersion = '2021' | '2025';

const EMFAC_VERSION_OPTIONS: { value: AbatementScatterEmfacVersion; label: string }[] = [
  { value: '2021', label: 'EMFAC 2021' },
  { value: '2025', label: 'EMFAC 2025' },
];

const BUILDINGS_OPTIONS: { value: BuildingsBauScenario; label: string }[] = [
  { value: 'conservative', label: 'Conservative buildings BAU' },
  { value: 'current_policy', label: 'Moderate buildings BAU (current policy)' },
  { value: 'accelerated', label: 'Aggressive buildings BAU (accelerated)' },
];

/** Presets for percentile clip (low & high inclusive; applied to X and Y separately). */
const CLIP_TAIL_PRESETS: { id: string; label: string; low: number; high: number }[] = [
  { id: 'full', label: '0–100% (min–max)', low: 0, high: 100 },
  { id: '1-99', label: '1st–99th percentile', low: 1, high: 99 },
  { id: '5-95', label: '5th–95th percentile', low: 5, high: 95 },
  { id: '10-90', label: '10th–90th percentile', low: 10, high: 90 },
  { id: '15-85', label: '15th–85th percentile', low: 15, high: 85 },
  { id: '25-75', label: '25th–75th percentile', low: 25, high: 75 },
];

export interface AbatementScatterPoint {
  locationName: string;
  kind: 'city' | 'county';
  x: number;
  y: number;
  reductionMt: number;
  population: number;
}

/** Hard caps so axis math and tick labels never blow up (e.g. handle drag outside plot). */
const ABATEMENT_AXIS_X_ABS_CAP = 5e6;
const ABATEMENT_AXIS_Y_ABS_CAP = 10000;

type AbatementDataAxisBounds = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

/**
 * Allowed scale domain: data extent plus modest padding. Used for zoom limits when axes are auto.
 */
function abatementScatterAxisBounds(points: AbatementScatterPoint[]): AbatementDataAxisBounds {
  if (!points.length) {
    return {
      xMin: -ABATEMENT_AXIS_X_ABS_CAP * 0.02,
      xMax: ABATEMENT_AXIS_X_ABS_CAP * 0.02,
      yMin: 0,
      yMax: 2,
    };
  }
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const p of points) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    xMin = Math.min(xMin, p.x);
    xMax = Math.max(xMax, p.x);
    yMin = Math.min(yMin, p.y);
    yMax = Math.max(yMax, p.y);
  }
  if (!Number.isFinite(xMin) || !Number.isFinite(yMin)) {
    return {
      xMin: -1e4,
      xMax: 1e6,
      yMin: 0,
      yMax: 2,
    };
  }
  const xSpan = Math.max(1e-6, xMax - xMin);
  const ySpan = Math.max(1e-12, yMax - yMin);
  const xPad = Math.max(xSpan * 0.35, 50);
  const yPad = Math.max(ySpan * 0.35, 0.05);
  return {
    xMin: Math.max(-ABATEMENT_AXIS_X_ABS_CAP, xMin - xPad),
    xMax: Math.min(ABATEMENT_AXIS_X_ABS_CAP, xMax + xPad),
    yMin: Math.max(-ABATEMENT_AXIS_Y_ABS_CAP, yMin - yPad),
    yMax: Math.min(ABATEMENT_AXIS_Y_ABS_CAP, yMax + yPad),
  };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Linear-interpolation percentile on a sorted array; p in [0, 1]. */
function percentileSorted(sorted: number[], p: number): number {
  if (sorted.length === 0) return NaN;
  const pp = clamp(p, 0, 1);
  if (sorted.length === 1) return sorted[0]!;
  const x = pp * (sorted.length - 1);
  const i0 = Math.floor(x);
  const i1 = Math.ceil(x);
  const t = x - i0;
  return sorted[i0]! * (1 - t) + sorted[i1]! * t;
}

function formatScatterXTick(v: number | string): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '';
  const a = Math.abs(v);
  if (a >= 1e6) return `$${v.toExponential(2)}`;
  if (a >= 1e4) return `$${Math.round(v).toLocaleString()}`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function rowToPoint(row: PublicationAbatementScatterRow): AbatementScatterPoint | null {
  const gt = (row.geography_type || '').trim();
  if (gt !== 'City' && gt !== 'County') return null;
  const cpt = row.cost_per_ton;
  const y = row.abatement_per_capita;
  const red = row.cumulative_reduction_mt;
  const pop = row.population;
  if (
    cpt == null ||
    y == null ||
    red == null ||
    pop == null ||
    !Number.isFinite(cpt) ||
    !Number.isFinite(y) ||
    !Number.isFinite(red) ||
    pop <= 0
  ) {
    return null;
  }
  return {
    locationName: row.location_name,
    kind: gt === 'County' ? 'county' : 'city',
    x: cpt,
    y,
    reductionMt: red,
    population: pop,
  };
}

type AxisClipRange = { xMin: number; xMax: number; yMin: number; yMax: number };

/** Default percentile window for tail clip (matches preset “1st–99th percentile”). */
const DEFAULT_ABATEMENT_TAIL_LOW_PCT = 1;
const DEFAULT_ABATEMENT_TAIL_HIGH_PCT = 99;

/** Shared X/Y percentile window (+ 4% padding each axis); null if not enough data. */
function axisRangeFromPercentileClip(
  points: AbatementScatterPoint[],
  lowPct: number,
  highPct: number
): AxisClipRange | null {
  if (!Number.isFinite(lowPct) || !Number.isFinite(highPct)) return null;
  if (lowPct < 0 || lowPct > 100 || highPct < 0 || highPct > 100) return null;
  if (lowPct >= highPct) return null;
  if (points.length < 2) return null;
  const xs = points
    .map((p) => p.x)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  const ys = points
    .map((p) => p.y)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (xs.length < 2 || ys.length < 2) return null;
  const pLo = lowPct / 100;
  const pHi = highPct / 100;
  let xLo = percentileSorted(xs, pLo);
  let xHi = percentileSorted(xs, pHi);
  let yLo = percentileSorted(ys, pLo);
  let yHi = percentileSorted(ys, pHi);
  const xPad = Math.max(1, (xHi - xLo) * 0.04);
  const yPad = Math.max(1e-8, (yHi - yLo) * 0.04);
  xLo -= xPad;
  xHi += xPad;
  yLo -= yPad;
  yHi += yPad;
  return {
    xMin: clamp(xLo, -ABATEMENT_AXIS_X_ABS_CAP, ABATEMENT_AXIS_X_ABS_CAP),
    xMax: clamp(xHi, -ABATEMENT_AXIS_X_ABS_CAP, ABATEMENT_AXIS_X_ABS_CAP),
    yMin: clamp(yLo, -ABATEMENT_AXIS_Y_ABS_CAP, ABATEMENT_AXIS_Y_ABS_CAP),
    yMax: clamp(yHi, -ABATEMENT_AXIS_Y_ABS_CAP, ABATEMENT_AXIS_Y_ABS_CAP),
  };
}

export const PublicationAbatementCostScatter: React.FC = () => {
  const chartRef = useRef<ChartJS<'scatter'> | null>(null);
  const pngPageOverride = useContext(ChartPngPageIdOverrideContext);
  const pngPageId = pngPageOverride ?? 'statewide_analysis';
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  const [buildingsBau, setBuildingsBau] = useState<BuildingsBauScenario>('current_policy');
  const [emfacVersion, setEmfacVersion] = useState<AbatementScatterEmfacVersion>('2021');
  const [loading, setLoading] = useState(true);
  const [rawRows, setRawRows] = useState<PublicationAbatementScatterRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  /** null = full padded data range; set only via percentile clip */
  const [axisClipRange, setAxisClipRange] = useState<AxisClipRange | null>(null);
  const [clipTailLowPct, setClipTailLowPct] = useState(1);
  const [clipTailHighPct, setClipTailHighPct] = useState(99);
  const [clipTailError, setClipTailError] = useState<string | null>(null);

  const clipTailPresetSelectValue = useMemo(() => {
    const m = CLIP_TAIL_PRESETS.find((p) => p.low === clipTailLowPct && p.high === clipTailHighPct);
    return m?.id ?? '';
  }, [clipTailLowPct, clipTailHighPct]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { rows, error: fetchErr } = await fetchPublicationAbatementScatterRows(
      emfacVersion,
      buildingsBau,
      PUBLICATION_ABATEMENT_POLICY_PRESET
    );
    if (fetchErr) {
      setError(fetchErr);
      setRawRows([]);
      setLastUpdated(null);
    } else {
      setRawRows(rows);
      const times = rows.map((r) => r.updated_at).filter(Boolean);
      if (times.length) {
        setLastUpdated(times.sort().slice(-1)[0] ?? null);
      } else {
        setLastUpdated(null);
      }
    }
    setLoading(false);
  }, [buildingsBau, emfacVersion]);

  useEffect(() => {
    void load();
  }, [load]);

  const { points, skippedInDb, stateRowsStored, skipReasonBreakdown, placeRowCount } = useMemo(() => {
    const pointsInner: AbatementScatterPoint[] = [];
    let skipped = 0;
    let stateN = 0;
    let placeRows = 0;
    const reasonCounts = new Map<string, number>();
    for (const row of rawRows) {
      const gt = (row.geography_type || '').trim();
      if (gt === 'State') {
        stateN += 1;
        continue;
      }
      placeRows += 1;
      const p = rowToPoint(row);
      if (p) pointsInner.push(p);
      else {
        skipped += 1;
        const key = (row.skip_reason && row.skip_reason.trim()) || 'missing_or_invalid_metrics';
        reasonCounts.set(key, (reasonCounts.get(key) ?? 0) + 1);
      }
    }
    const skipReasonBreakdown = Array.from(reasonCounts.entries()).sort((a, b) => b[1] - a[1]);
    return {
      points: pointsInner,
      skippedInDb: skipped,
      stateRowsStored: stateN,
      skipReasonBreakdown,
      placeRowCount: placeRows,
    };
  }, [rawRows]);

  const dataAxisBounds = useMemo(() => abatementScatterAxisBounds(points), [points]);

  const displayAxisBounds = useMemo((): AxisClipRange => {
    if (axisClipRange) return axisClipRange;
    return {
      xMin: dataAxisBounds.xMin,
      xMax: dataAxisBounds.xMax,
      yMin: dataAxisBounds.yMin,
      yMax: dataAxisBounds.yMax,
    };
  }, [axisClipRange, dataAxisBounds]);

  const zoomLimitsBounds = useMemo((): AbatementDataAxisBounds => {
    return {
      xMin: Math.min(dataAxisBounds.xMin, displayAxisBounds.xMin),
      xMax: Math.max(dataAxisBounds.xMax, displayAxisBounds.xMax),
      yMin: Math.min(dataAxisBounds.yMin, displayAxisBounds.yMin),
      yMax: Math.max(dataAxisBounds.yMax, displayAxisBounds.yMax),
    };
  }, [dataAxisBounds, displayAxisBounds]);

  const cityData = useMemo(
    () =>
      points
        .filter((p) => p.kind === 'city')
        .map((p) => ({
          x: p.x,
          y: p.y,
          locationName: p.locationName,
          reductionMt: p.reductionMt,
          population: p.population,
          kind: p.kind,
        })),
    [points]
  );
  const countyData = useMemo(
    () =>
      points
        .filter((p) => p.kind === 'county')
        .map((p) => ({
          x: p.x,
          y: p.y,
          locationName: p.locationName,
          reductionMt: p.reductionMt,
          population: p.population,
          kind: p.kind,
        })),
    [points]
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Cities',
          data: cityData,
          backgroundColor: 'rgba(0, 50, 98, 0.82)',
          borderColor: '#003262',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointStyle: 'rect' as const,
        },
        {
          label: 'Counties',
          data: countyData,
          backgroundColor: 'rgba(253, 181, 21, 0.88)',
          borderColor: '#FDB515',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointStyle: 'rect' as const,
        },
      ],
    }),
    [cityData, countyData]
  );

  const chartOptions: ChartOptions<'scatter'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', intersect: false },
      transitions: {
        zoom: { animation: { duration: 0 } },
        pan: { animation: { duration: 0 } },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const raw = items[0]?.raw as AbatementScatterPoint | undefined;
              return raw?.locationName ?? '';
            },
            label: (ctx) => {
              const raw = ctx.raw as AbatementScatterPoint;
              if (!raw?.locationName) return [];
              return [
                `Cost: $${raw.x.toFixed(0)}/t CO₂e`,
                `Abatement: ${raw.y.toFixed(3)} t CO₂e per capita`,
                `Cumulative abatement: ${raw.reductionMt.toFixed(0)} t CO₂e`,
                `Population: ${raw.population.toLocaleString()}`,
              ];
            },
          },
        },
        zoom: {
          limits: {
            x: {
              min: zoomLimitsBounds.xMin,
              max: zoomLimitsBounds.xMax,
              minRange: 1,
            },
            y: {
              min: zoomLimitsBounds.yMin,
              max: zoomLimitsBounds.yMax,
              minRange: 1e-8,
            },
          },
          pan: {
            enabled: false,
            mode: 'xy',
            modifierKey: 'ctrl',
          },
          zoom: {
            wheel: { enabled: true, speed: 0.06 },
            pinch: { enabled: true },
            mode: 'xy',
            drag: {
              enabled: true,
              threshold: 12,
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              borderColor: 'rgba(29, 78, 216, 0.85)',
              borderWidth: 1,
            },
          },
        },
      },
      scales: {
        x: axisClipRange
          ? {
              min: displayAxisBounds.xMin,
              max: displayAxisBounds.xMax,
              title: {
                display: true,
                text: 'Cumulative net cost per metric ton abated ($ / t CO₂e)',
              },
              ticks: {
                maxTicksLimit: 8,
                callback: (v) => formatScatterXTick(v as number),
              },
            }
          : {
              suggestedMin: dataAxisBounds.xMin,
              suggestedMax: dataAxisBounds.xMax,
              title: {
                display: true,
                text: 'Cumulative net cost per metric ton abated ($ / t CO₂e)',
              },
              ticks: {
                maxTicksLimit: 8,
                callback: (v) => formatScatterXTick(v as number),
              },
            },
        y: axisClipRange
          ? {
              min: displayAxisBounds.yMin,
              max: displayAxisBounds.yMax,
              title: {
                display: true,
                text: 'Cumulative abatement per capita (t CO₂e / person)',
              },
              ticks: {
                maxTicksLimit: 8,
                callback: (v) =>
                  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : String(v),
              },
            }
          : {
              suggestedMin: dataAxisBounds.yMin,
              suggestedMax: dataAxisBounds.yMax,
              title: {
                display: true,
                text: 'Cumulative abatement per capita (t CO₂e / person)',
              },
              ticks: {
                maxTicksLimit: 8,
                callback: (v) =>
                  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : String(v),
              },
            },
      },
    }),
    [axisClipRange, dataAxisBounds, displayAxisBounds, zoomLimitsBounds]
  );

  const handleResetAxes = useCallback(() => {
    const ch = chartRef.current;
    if (ch) {
      const reset = (ch as unknown as { resetZoom?: () => void }).resetZoom;
      if (typeof reset === 'function') {
        reset.call(ch);
      }
    }
    setAxisClipRange(null);
  }, []);

  const handleClipOutlierTails = useCallback(() => {
    setClipTailError(null);
    const lowPct = clipTailLowPct;
    const highPct = clipTailHighPct;
    if (!Number.isFinite(lowPct) || !Number.isFinite(highPct)) {
      setClipTailError('Use valid numbers for low and high percentiles.');
      return;
    }
    if (lowPct < 0 || lowPct > 100 || highPct < 0 || highPct > 100) {
      setClipTailError('Percentiles must be between 0 and 100.');
      return;
    }
    if (lowPct >= highPct) {
      setClipTailError('Low percentile must be less than high percentile.');
      return;
    }
    const range = axisRangeFromPercentileClip(points, lowPct, highPct);
    if (!range) return;
    setAxisClipRange(range);
    requestAnimationFrame(() => {
      const ch = chartRef.current;
      const reset = ch && (ch as unknown as { resetZoom?: () => void }).resetZoom;
      if (typeof reset === 'function') {
        reset.call(ch);
      }
    });
  }, [points, clipTailLowPct, clipTailHighPct]);

  const handleDownloadCsv = useCallback(() => {
    if (!points.length) return;
    const rows = points.map((p) => ({
      emfac_version: emfacVersion,
      buildings_bau: buildingsBau,
      policy_preset: PUBLICATION_ABATEMENT_POLICY_PRESET,
      location_name: p.locationName,
      geography_kind: p.kind,
      cost_per_ton_usd: p.x,
      abatement_per_capita_t_co2e: p.y,
      cumulative_reduction_mt: p.reductionMt,
      population: p.population,
    }));
    downloadDataAsCSV(
      rows,
      formatFilename('California', `Abatement_Scatter_${emfacVersion}_${buildingsBau}`)
    );
  }, [points, buildingsBau, emfacVersion]);

  const handleDownloadPng = useCallback(() => {
    downloadChartJsInstanceAsPNG(
      chartRef.current,
      formatFilename('California', `Abatement_Scatter_${emfacVersion}_${buildingsBau}`)
    );
  }, [buildingsBau, emfacVersion]);

  useEffect(() => {
    const id = PUBLICATION_ANALYSIS_CHART_IDS.abatementScatter;
    const label = 'Abatement cost vs. per-capita abatement';
    registerPng(id, label, handleDownloadPng, pngPageId);
    return () => unregisterPng(id);
  }, [registerPng, unregisterPng, handleDownloadPng, pngPageId]);

  /** New fetch or scenario → default 1–99% tail clip and clear zoom. */
  useEffect(() => {
    if (loading || points.length === 0) return;
    const id = requestAnimationFrame(() => {
      const range = axisRangeFromPercentileClip(
        points,
        DEFAULT_ABATEMENT_TAIL_LOW_PCT,
        DEFAULT_ABATEMENT_TAIL_HIGH_PCT
      );
      if (range) {
        setAxisClipRange(range);
        const ch = chartRef.current;
        const reset = ch && (ch as unknown as { resetZoom?: () => void }).resetZoom;
        if (typeof reset === 'function') {
          reset.call(ch);
        }
      }
    });
    return () => cancelAnimationFrame(id);
  }, [rawRows, loading, points]);

  return (
    <section className="publication-analysis-section publication-scatter-section" aria-labelledby="pub-scatter-abatement">
      <h2 id="pub-scatter-abatement">Abatement cost vs. per-capita abatement</h2>
      <p className="publication-analysis-intro">
        <strong>X</strong>: cumulative net cost per ton abated (2023–2045 vs state BAU). <strong>Y</strong>: abatement
        per capita. Data from <code>publication_abatement_scatter</code> (moderate policy preset). Transport-related
        inputs use the selected <strong>EMFAC {emfacVersion}</strong> slice. State row is stored but not plotted here.
      </p>

      <div className="publication-scatter-controls">
        <label className="publication-scatter-field">
          <span>Transport (EMFAC)</span>
          <select
            value={emfacVersion}
            onChange={(e) => setEmfacVersion(e.target.value as AbatementScatterEmfacVersion)}
            disabled={loading}
            aria-label="EMFAC version for transport inputs"
          >
            {EMFAC_VERSION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="publication-scatter-field">
          <span>Buildings BAU</span>
          <select
            value={buildingsBau}
            onChange={(e) => setBuildingsBau(e.target.value as BuildingsBauScenario)}
            disabled={loading}
          >
            {BUILDINGS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {!loading && points.length > 0 && (
          <>
            <button
              type="button"
              className="publication-scatter-reset-axes"
              onClick={handleResetAxes}
              title="Clear percentile clip and zoom; restore full padded data range"
            >
              Reset axes
            </button>
          </>
        )}
      </div>

      {!loading && points.length > 0 && (
        <div className="publication-scatter-clip-panel" role="group" aria-label="Percentile tail clip">
          <div className="publication-scatter-clip-panel__row">
            <label className="publication-scatter-field publication-scatter-field--compact">
              <span>Preset</span>
              <select
                value={clipTailPresetSelectValue}
                onChange={(e) => {
                  setClipTailError(null);
                  const v = e.target.value;
                  if (!v) return;
                  const p = CLIP_TAIL_PRESETS.find((x) => x.id === v);
                  if (p) {
                    setClipTailLowPct(p.low);
                    setClipTailHighPct(p.high);
                  }
                }}
                aria-label="Percentile clip preset"
              >
                <option value="">Custom (edit % below)</option>
                {CLIP_TAIL_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="publication-scatter-field publication-scatter-field--inline-num">
              <span>Low %</span>
              <input
                type="number"
                min={0}
                max={99}
                step={1}
                value={clipTailLowPct}
                onChange={(e) => {
                  setClipTailError(null);
                  const n = parseInt(e.target.value, 10);
                  if (Number.isFinite(n)) setClipTailLowPct(clamp(n, 0, 99));
                }}
                aria-label="Low percentile (0–99)"
              />
            </label>
            <label className="publication-scatter-field publication-scatter-field--inline-num">
              <span>High %</span>
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                value={clipTailHighPct}
                onChange={(e) => {
                  setClipTailError(null);
                  const n = parseInt(e.target.value, 10);
                  if (Number.isFinite(n)) setClipTailHighPct(clamp(n, 1, 100));
                }}
                aria-label="High percentile (1–100)"
              />
            </label>
            <button
              type="button"
              className="publication-scatter-clip-tails"
              onClick={handleClipOutlierTails}
              title="Set both axes from the chosen percentiles on X and Y (plus 4% padding). Points outside sit past the edges."
            >
              Clip tails
            </button>
          </div>
          <p className="publication-scatter-clip-panel__hint">
            Same low/high percentiles are applied independently to cost (X) and abatement (Y). Default is 1–99
            (applied when data loads; use <strong>Reset axes</strong> for the full range).
          </p>
          {clipTailError && <p className="publication-scatter-clip-panel__error">{clipTailError}</p>}
        </div>
      )}

      {!loading && points.length > 0 && (
        <p className="publication-scatter-zoom-hint" role="note">
          Use <strong>Clip tails</strong> to focus the axis range; box-drag / wheel zoom still works.{' '}
          <strong>Reset axes</strong> clears the clip and zoom.
        </p>
      )}

      {error && (
        <p className="publication-scatter-error">
          {error.includes('relation') || error.includes('does not exist')
            ? `${error} — Run sql/create_publication_abatement_scatter.sql and the populate script.`
            : error}
        </p>
      )}
      {!loading && !error && rawRows.length === 0 && (
        <p className="publication-scatter-placeholder">
          No rows for this scenario. Run{' '}
          <code>npx ts-node --transpile-only scripts/populate_publication_abatement_scatter.ts</code> from{' '}
          <code>my-app/frontend</code> (with <code>SUPABASE_SERVICE_ROLE_KEY</code>).
        </p>
      )}
      {!loading && rawRows.length > 0 && (
        <p className="publication-scatter-meta">
          Loaded {rawRows.length} DB row(s) for this scenario ({placeRowCount} city/county, {stateRowsStored} state).
          Plotting {points.length} points ({cityData.length} cities, {countyData.length} counties).
          {skippedInDb > 0 && (
            <>
              {' '}
              {skippedInDb} city/county row(s) not plotted.
              {skipReasonBreakdown.length > 0 && (
                <span className="publication-scatter-meta--reasons">
                  {' '}
                  Reasons:{' '}
                  {skipReasonBreakdown.map(([reason, n]) => `${reason} (${n})`).join('; ')}.
                </span>
              )}
            </>
          )}
          {lastUpdated ? ` Last updated (max): ${new Date(lastUpdated).toLocaleString()}.` : ''}
        </p>
      )}

      <div className="publication-scatter-chart-wrap">
        {loading && <p className="publication-scatter-placeholder">Loading precomputed data…</p>}
        {!loading && points.length > 0 && (
          <div className="publication-figure-frame publication-scatter-chart-figure">
            <div className="publication-scatter-chart-inner">
              <Scatter ref={chartRef} data={chartData} options={chartOptions} />
            </div>
            <div className="publication-scatter-dataset-legend" aria-label="Series">
              <span className="publication-scatter-dataset-legend-item">
                <span
                  className="publication-scatter-dataset-legend-swatch"
                  style={{ backgroundColor: 'rgba(0, 50, 98, 0.82)', borderColor: '#003262' }}
                  aria-hidden
                />
                Cities
              </span>
              <span className="publication-scatter-dataset-legend-item">
                <span
                  className="publication-scatter-dataset-legend-swatch"
                  style={{ backgroundColor: 'rgba(253, 181, 21, 0.88)', borderColor: '#FDB515' }}
                  aria-hidden
                />
                Counties
              </span>
            </div>
            <PublicationFigureDownloads
              onCsv={handleDownloadCsv}
              onPng={handleDownloadPng}
              csvDisabled={loading}
              pngDisabled={loading}
              ariaLabel="Download abatement scatter"
            />
          </div>
        )}
        {!loading && !error && rawRows.length > 0 && points.length === 0 && (
          <p className="publication-scatter-placeholder">No valid city/county points for this scenario slice.</p>
        )}
      </div>
    </section>
  );
};

export default PublicationAbatementCostScatter;
